import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Gift, Loader2, ArrowRight, Sparkles, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { getStoredToken } from '../App';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * /redeem — Users enter a promo code to get free credits.
 *
 * UX niceties:
 * - Auto-formats as user types: strips spaces, uppercases, inserts dashes
 * - Accepts `?code=` query param (influencers can share a direct-link)
 * - Confetti + celebration screen on success → auto-redirects to dashboard
 */
export default function Redeem() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null); // { credits_added, campaign_name }
  const [error, setError] = useState('');

  // Pre-fill from URL ?code=WIM-TIKTOK-4X7Q
  useEffect(() => {
    const urlCode = searchParams.get('code');
    if (urlCode) setCode(formatCode(urlCode));
  }, [searchParams]);

  const formatCode = (raw) => {
    // Strip everything non-alphanumeric, uppercase, then add dashes for the common patterns
    const cleaned = (raw || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
    // Re-insert dashes: WIM-{TAG max 10}-{4 chars}
    // If starts with WIM, break after WIM then after tag (look for first sequence ≤ 10 alphanumerics)
    if (!cleaned.startsWith('WIM')) {
      // Graceful: let user type anything, still format what we have
      return cleaned.slice(0, 24);
    }
    const rest = cleaned.slice(3);
    if (rest.length === 0) return 'WIM';
    // Tag is everything up to the last 4 chars (suffix)
    if (rest.length <= 4) return `WIM-${rest}`;
    const tag = rest.slice(0, rest.length - 4);
    const suffix = rest.slice(rest.length - 4);
    return `WIM-${tag}-${suffix}`;
  };

  const handleChange = (e) => {
    setCode(formatCode(e.target.value));
    setError('');
  };

  const handleRedeem = async () => {
    if (!code || code.length < 8) {
      setError('Enter a valid code');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const token = getStoredToken();
      if (!token) {
        toast.error('Please log in to redeem codes');
        navigate('/login?redirect=/redeem?code=' + encodeURIComponent(code));
        return;
      }
      const res = await fetch(`${API}/api/promo/redeem`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ code }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.detail || 'Unable to redeem this code');
        return;
      }
      setSuccess(data);
      toast.success(data.message || `+${data.credits_added} credits added! 🎉`);
      // Auto-redirect to dashboard after 4 seconds
      setTimeout(() => navigate('/dashboard'), 4000);
    } catch (err) {
      setError('Network error — please try again');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !loading && code) handleRedeem();
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" data-testid="redeem-success">
        <div className="relative max-w-md w-full">
          {/* Confetti burst via CSS */}
          <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
            {[...Array(30)].map((_, i) => (
              <div
                key={i}
                className="absolute w-2 h-2 rounded-full animate-confetti"
                style={{
                  left: `${Math.random() * 100}%`,
                  backgroundColor: ['#a78bfa', '#f472b6', '#fbbf24', '#34d399', '#60a5fa'][i % 5],
                  animationDelay: `${Math.random() * 0.5}s`,
                  animationDuration: `${2 + Math.random() * 1.5}s`,
                }}
              />
            ))}
          </div>
          <div className="glass-card p-8 text-center relative z-10">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 mb-6">
              <CheckCircle2 className="h-12 w-12 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-3">Code Redeemed!</h1>
            <p className="text-5xl font-black bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent mb-4">
              +{success.credits_added}
            </p>
            <p className="text-zinc-300 text-lg mb-2">credits added to your account</p>
            {success.campaign_name && (
              <p className="text-zinc-500 text-sm mb-6">Gift from: {success.campaign_name}</p>
            )}
            <Button
              onClick={() => navigate('/dashboard')}
              className="btn-primary w-full"
              data-testid="redeem-to-dashboard"
            >
              Start Creating <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <p className="text-zinc-600 text-xs mt-4">Redirecting to dashboard…</p>
          </div>
        </div>
        {/* Inline keyframes for confetti */}
        <style>{`
          @keyframes confetti {
            0%   { transform: translateY(-20px) rotate(0deg); opacity: 1; }
            100% { transform: translateY(400px) rotate(720deg); opacity: 0; }
          }
          .animate-confetti { animation: confetti 2.5s linear forwards; }
        `}</style>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4" data-testid="redeem-page">
      <div className="max-w-md w-full">
        <div className="glass-card p-8">
          <div className="flex items-center justify-center mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-purple-500/20 to-pink-500/20 border border-purple-500/30">
              <Gift className="h-8 w-8 text-purple-400" />
            </div>
          </div>

          <h1 className="text-3xl font-bold text-white text-center mb-2">Redeem a Gift Code</h1>
          <p className="text-zinc-400 text-center text-sm mb-8">
            Got a code from a creator? Enter it below to add credits to your account.
          </p>

          <div className="mb-4">
            <label className="text-sm text-zinc-300 mb-2 block font-medium">Your Code</label>
            <Input
              type="text"
              value={code}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              placeholder="WIM-TIKTOK-4X7Q"
              autoFocus
              className="input-dark text-center text-lg tracking-wider font-mono uppercase"
              maxLength={24}
              data-testid="redeem-code-input"
            />
            {error && (
              <p className="text-red-400 text-sm mt-2" data-testid="redeem-error">
                {error}
              </p>
            )}
          </div>

          <Button
            onClick={handleRedeem}
            disabled={loading || !code}
            className="btn-primary w-full mb-4"
            data-testid="redeem-submit-btn"
          >
            {loading ? (
              <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Redeeming…</>
            ) : (
              <><Sparkles className="mr-2 h-4 w-4" /> Redeem Code</>
            )}
          </Button>

          <p className="text-xs text-zinc-600 text-center leading-relaxed">
            Promotional codes have no cash value, cannot be transferred or sold,
            and may be modified or revoked at any time. One code per user per campaign.
          </p>
        </div>

        <div className="text-center mt-6">
          <button
            onClick={() => navigate('/dashboard')}
            className="text-zinc-500 hover:text-zinc-300 text-sm transition-colors"
            data-testid="redeem-back-link"
          >
            ← Back to Dashboard
          </button>
        </div>
      </div>
    </div>
  );
}
